import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Icons
import CodeIcon from '@mui/icons-material/Code';
import CloudIcon from '@mui/icons-material/Cloud';
import StorageIcon from '@mui/icons-material/Storage';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import PublicIcon from '@mui/icons-material/Public';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SchoolIcon from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import TerminalIcon from '@mui/icons-material/Terminal';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BugReportIcon from '@mui/icons-material/BugReport';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

const WebDevelopment: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Technologies
  const frontendTechnologies = [
    { name: "React", icon: <CodeIcon /> },
    { name: "Angular", icon: <CodeIcon /> },
    { name: "Vue.js", icon: <CodeIcon /> },
    { name: "Next.js", icon: <CodeIcon /> },
    { name: "TypeScript", icon: <CodeIcon /> },
    { name: "Material UI", icon: <DesignServicesIcon /> },
    { name: "Tailwind CSS", icon: <DesignServicesIcon /> },
    { name: "SASS/SCSS", icon: <DesignServicesIcon /> }
  ];

  const backendTechnologies = [
    { name: "Node.js", icon: <TerminalIcon /> },
    { name: ".NET Core", icon: <TerminalIcon /> },
    { name: "Java Spring", icon: <TerminalIcon /> },
    { name: "Python Django", icon: <TerminalIcon /> },
    { name: "PHP Laravel", icon: <TerminalIcon /> },
    { name: "GraphQL", icon: <StorageIcon /> },
    { name: "RESTful APIs", icon: <IntegrationInstructionsIcon /> }
  ];

  const databaseTechnologies = [
    { name: "SQL Server", icon: <StorageIcon /> },
    { name: "MySQL", icon: <StorageIcon /> },
    { name: "PostgreSQL", icon: <StorageIcon /> },
    { name: "MongoDB", icon: <StorageIcon /> },
    { name: "Firebase", icon: <StorageIcon /> },
    { name: "Redis", icon: <StorageIcon /> }
  ];

  // Web Application Types
  const webAppTypes = [
    {
      title: "Corporate Websites",
      description: "Professional web presence for your business with a focus on brand identity, information delivery, and lead generation",
      icon: <BusinessCenterIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: "E-commerce Platforms",
      description: "Feature-rich online stores with secure payment processing, inventory management, and customer relationship tools",
      icon: <ShoppingCartIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: "Web Portals & Intranets",
      description: "Secure internal systems for document management, communication, and business process automation",
      icon: <PublicIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: "Enterprise Applications",
      description: "Custom business applications to streamline operations, manage resources, and improve decision making",
      icon: <BusinessCenterIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: "Educational Platforms",
      description: "Learning management systems and educational resources with interactive content delivery",
      icon: <SchoolIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: "Healthcare Solutions",
      description: "Patient portals, medical record systems, and healthcare management applications",
      icon: <LocalHospitalIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    }
  ];

  // Development Process
  const developmentProcess = [
    {
      step: "Discovery & Planning",
      description: "Understanding your requirements, business objectives, and user needs to create a comprehensive project plan",
      icon: <BusinessCenterIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "UX/UI Design",
      description: "Creating intuitive, engaging user interfaces that enhance user experience and align with your brand",
      icon: <DesignServicesIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "Development",
      description: "Building your web application using modern technologies and development practices",
      icon: <CodeIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "Testing & QA",
      description: "Ensuring your application is reliable, secure, and performs well across all devices and browsers",
      icon: <BugReportIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "Deployment",
      description: "Launching your application with proper configuration and optimization for performance",
      icon: <CloudIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "Maintenance & Support",
      description: "Providing ongoing updates, security patches, and feature enhancements to keep your application current",
      icon: <SupportAgentIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    }
  ];

  // Key features
  const keyFeatures = [
    {
      title: "Responsive Design",
      description: "Websites that look and perform beautifully across all devices and screen sizes",
      icon: <MobileFriendlyIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: "Performance Optimization",
      description: "Fast-loading pages and optimized application performance for superior user experience",
      icon: <SpeedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: "SEO Best Practices",
      description: "Built-in search engine optimization to improve visibility and organic traffic",
      icon: <PublicIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: "Security Focus",
      description: "Advanced security measures to protect against common vulnerabilities and threats",
      icon: <SecurityIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Web Development Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Creating powerful, scalable web solutions for businesses of all sizes
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Modern Web Solutions for the Digital Age
              </Typography>
              <Typography paragraph>
                In today's digital-first world, your web presence is often the first point of contact with
                customers and stakeholders. Right Track Technologies Limited delivers cutting-edge web development
                solutions that create compelling digital experiences, drive user engagement, and achieve your
                business objectives.
              </Typography>
              <Typography paragraph>
                Our expert team creates everything from responsive corporate websites to complex enterprise 
                web applications, e-commerce platforms, and custom portals. We leverage modern frameworks and 
                technologies to build scalable, secure, and high-performance web solutions that drive your business forward.
              </Typography>
              <Typography paragraph>
                Whether you're looking to establish a new web presence, modernize an existing site, or build a 
                complex web application, our development expertise ensures your project delivers exceptional results.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Web Development"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Technologies Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Technology Stack
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              We use the latest technologies and frameworks to build robust, scalable web applications
            </Typography>
          </motion.div>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Frontend Technologies */}
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" component="h3" fontWeight="bold" color="#0c2340">
                  Frontend Technologies
                </Typography>
                <Divider sx={{ mt: 1, mb: 3 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {frontendTechnologies.map((tech, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: 'white', 
                        p: 1, 
                        pl: 2,
                        pr: 3,
                        borderRadius: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        mr: 1,
                        mb: 1
                      }}
                    >
                      <Box sx={{ color: theme.palette.primary.main, mr: 1 }}>
                        {tech.icon}
                      </Box>
                      <Typography fontWeight="medium">
                        {tech.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>

            {/* Backend Technologies */}
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" component="h3" fontWeight="bold" color="#0c2340">
                  Backend Technologies
                </Typography>
                <Divider sx={{ mt: 1, mb: 3 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {backendTechnologies.map((tech, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: 'white', 
                        p: 1, 
                        pl: 2,
                        pr: 3,
                        borderRadius: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        mr: 1,
                        mb: 1
                      }}
                    >
                      <Box sx={{ color: theme.palette.primary.main, mr: 1 }}>
                        {tech.icon}
                      </Box>
                      <Typography fontWeight="medium">
                        {tech.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>

            {/* Database Technologies */}
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" component="h3" fontWeight="bold" color="#0c2340">
                  Database & Storage Solutions
                </Typography>
                <Divider sx={{ mt: 1, mb: 3 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {databaseTechnologies.map((tech, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: 'white', 
                        p: 1, 
                        pl: 2,
                        pr: 3,
                        borderRadius: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        mr: 1,
                        mb: 1
                      }}
                    >
                      <Box sx={{ color: theme.palette.primary.main, mr: 1 }}>
                        {tech.icon}
                      </Box>
                      <Typography fontWeight="medium">
                        {tech.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Web Application Types Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Web Solutions We Deliver
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Custom web applications tailored to your specific business needs
          </Typography>
        </motion.div>
      
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 4,
          justifyContent: 'center'
        }}>
          {webAppTypes.map((type, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {type.icon}
                </Box>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  {type.title}
                </Typography>
                <Typography color="text.secondary">
                  {type.description}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Development Process Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Development Process
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              A structured approach to delivering high-quality web projects
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 4
          }}>
            {developmentProcess.map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: 3,
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Box 
                    sx={{ 
                      bgcolor: 'rgba(12, 35, 64, 0.1)', 
                      p: 2, 
                      borderRadius: '50%', 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '60px',
                      height: '60px'
                    }}
                  >
                    {process.icon}
                  </Box>
                  <Box>
                    <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                      {`${index + 1}. ${process.step}`}
                    </Typography>
                    <Typography color="text.secondary">
                      {process.description}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Key Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Key Features of Our Web Applications
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Quality and excellence at every level
          </Typography>
        </motion.div>

        <Box sx={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          justifyContent: 'center',
        }}>
          {keyFeatures.map((feature, index) => (
            <Box key={index} sx={{ width: { xs: '100%', md: '45%' }, px: 2, mb: 4 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ mr: 2, mt: 0.5 }}>
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                      {feature.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ bgcolor: '#0c2340', color: 'white', py: 10 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center' }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Ready to Start Your Web Project?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9 }}>
              Contact us today to discuss your web development needs and discover how 
              our solutions can help your business thrive online.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#0c2340',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Start Your Project
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default WebDevelopment; 